/**
 * Field handlers hook - 3-way dispatcher (NC, MAS Direct, MAS Indirect) for handleFieldChange.
 */
import { useCallback } from 'react';
import { markUserChangesIfNeeded, isGlobalField } from '../shared/utils/fieldHandlerCore';
import { applyNCFieldChange } from '../templates/NC/ncFieldHandlers';
import { applyMASDirectFieldChange } from '../templates/MASDirect/masDirectFieldHandlers';
import { applyMASIndirectFieldChange } from '../templates/MASIndirect/masIndirectFieldHandlers';
import { validateTargetLaunchDate } from '../bidAnalysisTemplate.logic';
import type { UseFieldHandlersDeps, UseFieldHandlersReturn } from './useFieldHandlers.types';

export function useFieldHandlers(deps: UseFieldHandlersDeps): UseFieldHandlersReturn {
  const {
    bidDetails,
    activePackSizeTab,
    activeYearTab,
    yearFormData,
    formData,
    setYearFormData,
    yearFormDataRef,
    setPackSizeFormData,
    setFormData,
    setValidationErrors,
    hasUserMadeChangesRef,
    isInitialLoadRef,
    validateField,
  } = deps;

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      const currentTemplateType = bidDetails?.template_type || 'mas';
      const isNC = currentTemplateType === 'nc';
      const productType = bidDetails?.product_type;
      const isDirectProduct = productType === 'direct';

      markUserChangesIfNeeded(fieldName, { hasUserMadeChangesRef, isInitialLoadRef });

      const processedValue = value;
      const isGlobal = isGlobalField(fieldName);
      const isGsmsHndlgType = fieldName === 'gsmsHndlgType';

      if (isNC) {
        applyNCFieldChange(fieldName, processedValue, {
          activePackSizeTab,
          activeYearTab,
          yearFormData,
          formData,
          setYearFormData,
          yearFormDataRef,
          setFormData,
          isGlobalField: isGlobal,
          isGsmsHndlgType,
          isDirectProduct,
        });
      } else if (isDirectProduct) {
        applyMASDirectFieldChange(fieldName, processedValue, {
          activePackSizeTab: activePackSizeTab ?? 0,
          setPackSizeFormData,
          isGlobalField: isGlobal,
          isGsmsHndlgType,
          isDirectProduct,
        });
      } else {
        applyMASIndirectFieldChange(fieldName, processedValue, {
          activePackSizeTab: activePackSizeTab ?? 0,
          setPackSizeFormData,
          isGlobalField: isGlobal,
          isGsmsHndlgType,
          isDirectProduct,
        });
      }

      let defaultTargetLaunchDate: Date | null = null;
      if (fieldName === 'analysisDate' && processedValue && !formData.targetLaunchDate) {
        try {
          let analysisDateObj: Date;
          if (processedValue instanceof Date) {
            analysisDateObj = new Date(processedValue.getTime());
          } else if (typeof processedValue === 'string') {
            analysisDateObj = new Date(processedValue);
          } else {
            analysisDateObj = new Date(processedValue);
          }
          if (!isNaN(analysisDateObj.getTime())) {
            defaultTargetLaunchDate = new Date(analysisDateObj);
            defaultTargetLaunchDate.setMonth(defaultTargetLaunchDate.getMonth() + 6);
          }
        } catch {
          // ignore
        }
      }

      setFormData((prev: any) => {
        const newFormData = { ...prev, [fieldName]: processedValue };
        if (defaultTargetLaunchDate) newFormData.targetLaunchDate = defaultTargetLaunchDate;
        return newFormData;
      });

      if (currentTemplateType === 'nc' && fieldName === 'targetLaunchDate') {
        const valueToSync = processedValue;
        setYearFormData(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(packKey => {
            const packIndex = parseInt(packKey, 10);
            if (isNaN(packIndex) || next[packIndex] == null) return;
            const packYears = { ...next[packIndex] };
            if (packYears[0] != null) {
              packYears[0] = { ...packYears[0], targetLaunchDate: valueToSync };
              next[packIndex] = packYears;
            }
          });
          return next;
        });
        const refNext = { ...yearFormDataRef.current };
        Object.keys(refNext).forEach(packKey => {
          const packIndex = parseInt(packKey, 10);
          if (isNaN(packIndex) || refNext[packIndex] == null) return;
          const packYears = { ...refNext[packIndex] };
          if (packYears[0] != null) {
            packYears[0] = { ...packYears[0], targetLaunchDate: valueToSync };
            refNext[packIndex] = packYears;
          }
        });
        yearFormDataRef.current = refNext;
      }

      if (fieldName === 'targetLaunchDate') {
        const error = validateTargetLaunchDate(processedValue, formData.analysisDate);
        if (error) {
          setValidationErrors(prev => ({ ...prev, targetLaunchDate: error }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.targetLaunchDate;
            return newErrors;
          });
        }
      }

      if (fieldName === 'analysisDate') {
        const error = validateTargetLaunchDate(formData.targetLaunchDate, processedValue);
        if (error) {
          setValidationErrors(prev => ({ ...prev, targetLaunchDate: error }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.targetLaunchDate;
            return newErrors;
          });
        }
      }

      const error = validateField(fieldName, processedValue);
      if (error) {
        setValidationErrors(prev => ({ ...prev, [fieldName]: error }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [
      bidDetails,
      activePackSizeTab,
      activeYearTab,
      yearFormData,
      formData,
      setYearFormData,
      yearFormDataRef,
      setPackSizeFormData,
      setFormData,
      setValidationErrors,
      hasUserMadeChangesRef,
      isInitialLoadRef,
      validateField,
    ]
  );

  return { handleFieldChange };
}
